import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import "./PersonaManager.css";
import { PersonaCard } from "./PersonaCard";
import { PersonaExpanded } from "winslow-core";
import { apiGetPersonas } from "../../services/PersonasService";
import { selectUser, selectHasNativeHRIS } from "../../redux/features/appData";
import { apiGetAllEmployees } from "../../services/EmployeeService";
import {
  apiGetFlattenedEmployeeRecordsFromWorkspace,
  apiGetCustomPropertiesForWorkspace,
} from "../../services/EmployeeRecordService";

import { PersonaCreator } from "./PersonaCreator";
import { EmployeeCacheProvider } from "./EmployeeCacheProvider";
import { EmployeeRecordCacheProvider } from "./EmployeeRecordCacheProvider";
import { useEmployeeCache } from "../../hooks/useEmployeeCache";
import { useEmployeeRecordCache } from "../../hooks/useEmployeeRecordCache";
import { Button } from "../../ds2/components";
import { PlusIcon } from "lucide-react";

const PersonaManagerWithEmployeeData: React.FC = () => {
  const storeUser = useSelector(selectUser);
  const { setEmployees, setIsLoaded, employees } = useEmployeeCache();

  useEffect(() => {
    const loadEmployeeData = async () => {
      if (storeUser?.currentWorkspaceId) {
        try {
          const employees = await apiGetAllEmployees(
            storeUser.currentWorkspaceId
          );
          if (employees) {
            setEmployees(employees);
            setIsLoaded(true);
          }
        } catch (error) {
          console.error("Failed to load employees for suggestions:", error);
        }
      }
    };

    loadEmployeeData();
  }, [storeUser?.currentWorkspaceId, setEmployees, setIsLoaded]);

  return (
    <PersonaManagerContent
      employees={employees}
      employeeRecords={[]}
      customProperties={[]}
      useNativeHRIS={false}
    />
  );
};

const PersonaManagerWithEmployeeRecordData: React.FC = () => {
  const storeUser = useSelector(selectUser);
  const {
    setEmployeeRecords,
    setCustomProperties,
    setIsLoaded,
    employeeRecords,
    customProperties,
  } = useEmployeeRecordCache();

  useEffect(() => {
    const loadEmployeeRecordData = async () => {
      if (storeUser?.currentWorkspaceId) {
        try {
          const [flattenedString, customProperties] = await Promise.all([
            apiGetFlattenedEmployeeRecordsFromWorkspace(
              storeUser.currentWorkspaceId
            ),
            apiGetCustomPropertiesForWorkspace(storeUser.currentWorkspaceId),
          ]);

          if (flattenedString) {
            const parsedRecords = JSON.parse(flattenedString);
            setEmployeeRecords(parsedRecords);
          }
          if (customProperties) {
            setCustomProperties(customProperties);
          }
          setIsLoaded(true);
        } catch (error) {
          console.error(
            "Failed to load employee records for suggestions:",
            error
          );
        }
      }
    };

    loadEmployeeRecordData();
  }, [
    storeUser?.currentWorkspaceId,
    setEmployeeRecords,
    setCustomProperties,
    setIsLoaded,
  ]);

  return (
    <PersonaManagerContent
      employees={[]}
      employeeRecords={employeeRecords}
      customProperties={customProperties}
      useNativeHRIS={true}
    />
  );
};

interface PersonaManagerContentProps {
  employees: any[];
  employeeRecords: any[];
  customProperties: any[];
  useNativeHRIS: boolean;
}

function PersonaManagerContent({
  employees,
  employeeRecords,
  customProperties,
  useNativeHRIS,
}: PersonaManagerContentProps) {
  const storeUser = useSelector(selectUser);

  const [personas, setPersonas] = useState<PersonaExpanded[]>([]);

  const [isCreatorOpen, setIsCreatorOpen] = useState(false);

  const [selectedPersona, setSelectedPersona] =
    useState<PersonaExpanded | null>(null);

  const handlePersonaClick = (persona: PersonaExpanded) => {
    setSelectedPersona(persona);
    setIsCreatorOpen(true);
  };

  const refreshPersonas = useCallback(() => {
    if (storeUser && storeUser.currentWorkspaceId) {
      apiGetPersonas(storeUser.currentWorkspaceId).then((personaResults) => {
        if (personaResults) {
          setPersonas(
            personaResults.sort((a, b) => a.name.localeCompare(b.name))
          );
        }
      });
    }
  }, [storeUser]);

  const handleCreatorClose = () => {
    setIsCreatorOpen(false);
    setSelectedPersona(null);
  };

  useEffect(() => {
    refreshPersonas();
  }, [storeUser, refreshPersonas]);

  return (
    <div className="persona-manager-container">
      <div className="persona-manager-header">
        {/* <h1 className="persona-manager-title">Manage Personas</h1> */}
        <div className="page-title">Personas</div>

        <Button
          variant="primary"
          leftIcon={<PlusIcon size="16" />}
          onClick={() => {
            setSelectedPersona(null);
            setIsCreatorOpen(true);
          }}>
          Persona
        </Button>

        {/* <button
          className="persona-manager-new-button"
          onClick={() => {
            setSelectedPersona(null);
            setIsCreatorOpen(true);
          }}>
          + New Persona
        </button> */}
      </div>
      <div className="persona-manager-cards-container">
        <div className="persona-manager-card-grid">
          {personas.map((persona) => (
            <div key={persona.id} className="persona-manager-card-column">
              <PersonaCard onClick={handlePersonaClick} persona={persona} />
            </div>
          ))}
        </div>
      </div>
      <PersonaCreator
        isOpen={isCreatorOpen}
        onClose={handleCreatorClose}
        existingPersona={selectedPersona}
        onPersonaSaved={refreshPersonas}
        employees={employees}
        employeeRecords={employeeRecords}
        customProperties={customProperties}
        useNativeHRIS={useNativeHRIS}
      />
    </div>
  );
}

export default function PersonaManager() {
  const hasNativeHRIS = useSelector(selectHasNativeHRIS);

  if (hasNativeHRIS) {
    return (
      <EmployeeRecordCacheProvider>
        <PersonaManagerWithEmployeeRecordData />
      </EmployeeRecordCacheProvider>
    );
  }

  return (
    <EmployeeCacheProvider>
      <PersonaManagerWithEmployeeData />
    </EmployeeCacheProvider>
  );
}
